// src/features/companies/components/CompanyList.jsx
import React, { useState, useEffect } from 'react';
import { 
	PencilIcon, 
	TrashIcon, 
	BuildingOfficeIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../hooks/useAuth';

const CompanyList = ({ users, companies, onEdit, onDelete }) => {
	// Estado para la paginación
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [mobileItemsPerPage, setMobileItemsPerPage] = useState(5);
	const { isManager } = useAuth();
	// Determinar cuántos items mostrar según el tamaño de pantalla
	const getEffectiveItemsPerPage = () => {
		return window.innerWidth < 768 ? mobileItemsPerPage : itemsPerPage;
	};

	// Calcular datos de paginación
	const totalItems = companies.length;
	const effectiveItemsPerPage = getEffectiveItemsPerPage();
	const totalPages = Math.ceil(totalItems / effectiveItemsPerPage);
  
	// Calcular índices para la página actual
	const startIndex = (currentPage - 1) * effectiveItemsPerPage;
	const endIndex = Math.min(startIndex + effectiveItemsPerPage, totalItems);
	const currentCompanies = companies.slice(startIndex, endIndex);

	// Resetear a página 1 cuando cambian los datos o items por página
	useEffect(() => {
		setCurrentPage(1);
	}, [companies.length, itemsPerPage, mobileItemsPerPage]);

	// Efecto para detectar cambios en el tamaño de pantalla
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768 && currentPage > Math.ceil(totalItems / mobileItemsPerPage)) {
				setCurrentPage(1);
			}
		};
    
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
  	}, [currentPage, totalItems, mobileItemsPerPage]);

	// Navegación de páginas
	const goToPage = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const goToFirstPage = () => goToPage(1);
	const goToLastPage = () => goToPage(totalPages);
	const goToPrevPage = () => goToPage(currentPage - 1);
	const goToNextPage = () => goToPage(currentPage + 1);

	// Manejo de eliminación
	const handleDelete = async (company) => {
		if (window.confirm(`¿Estás seguro de que quieres eliminar la empresa "${company.nombre}"?`)) {
			try {
				await onDelete(company.nit);
				// Si eliminamos el último item de la página y no es la página 1, retroceder
				if (currentCompanies.length === 1 && currentPage > 1) {
					setCurrentPage(currentPage - 1);
				}
			} catch (error) {
				console.error('Error eliminando empresa:', error);
			}
		}
	};

	// Componente de paginación para desktop
	const DesktopPagination = () => (
		<div className="hidden md:flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
			<div className="flex flex-1 justify-between sm:hidden">
				<button
					onClick={goToPrevPage}
					disabled={currentPage === 1}
					className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
						currentPage === 1
						? 'bg-gray-100 text-gray-400 cursor-not-allowed'
						: 'bg-white text-gray-700 hover:bg-gray-50'
					}`}
				>
					Anterior
				</button>
				<button
					onClick={goToNextPage}
					disabled={currentPage === totalPages}
					className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
						currentPage === totalPages
						? 'bg-gray-100 text-gray-400 cursor-not-allowed'
						: 'bg-white text-gray-700 hover:bg-gray-50'
					}`}
				>
					Siguiente
				</button>
			</div>
		
			<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div>
					<p className="text-sm text-gray-700">
						Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
						<span className="font-medium">{endIndex}</span> de{' '}
						<span className="font-medium">{totalItems}</span> empresas
					</p>
				</div>
				
				<div className="flex items-center space-x-2">
					{/* Selector de items por página */}
					<div className="flex items-center">
						<label htmlFor="items-per-page" className="text-sm text-gray-700 mr-2">
							Mostrar:
						</label>
						<select
							id="items-per-page"
							value={itemsPerPage}
							onChange={(e) => setItemsPerPage(Number(e.target.value))}
							className="block w-20 py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
						>
							{[5, 10, 20, 50].map((size) => (
								<option key={size} value={size}>
									{size}
								</option>
							))}
						</select>
					</div>

					{/* Navegación de páginas */}
					<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
						<button
							onClick={goToFirstPage}
							disabled={currentPage === 1}
							className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
								currentPage === 1
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-white text-gray-500 hover:bg-gray-50'
							}`}
						>
							<span className="sr-only">Primera página</span>
							<ChevronDoubleLeftIcon className="h-4 w-4" />
						</button>
						
						<button
							onClick={goToPrevPage}
							disabled={currentPage === 1}
							className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium ${
								currentPage === 1
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-white text-gray-500 hover:bg-gray-50'
							}`}
						>
							<span className="sr-only">Anterior</span>
							<ChevronLeftIcon className="h-4 w-4" />
						</button>

						{/* Números de página */}
						{(() => {
							const pageNumbers = [];
							const maxVisiblePages = 5;
							let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
							let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
							
							if (endPage - startPage + 1 < maxVisiblePages) {
								startPage = Math.max(1, endPage - maxVisiblePages + 1);
							}
							
							for (let i = startPage; i <= endPage; i++) {
								pageNumbers.push(i);
							}
							
							return pageNumbers.map((number) => (
								<button
									key={number}
									onClick={() => goToPage(number)}
									className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
										currentPage === number
										? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
										: 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
									}`}
								>
									{number}
								</button>
							));
						})()}

						<button
							onClick={goToNextPage}
							disabled={currentPage === totalPages}
							className={`relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium ${
								currentPage === totalPages
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-white text-gray-500 hover:bg-gray-50'
							}`}
						>
							<span className="sr-only">Siguiente</span>
							<ChevronRightIcon className="h-4 w-4" />
						</button>
						
						<button
							onClick={goToLastPage}
							disabled={currentPage === totalPages}
							className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
								currentPage === totalPages
								? 'bg-gray-100 text-gray-400 cursor-not-allowed'
								: 'bg-white text-gray-500 hover:bg-gray-50'
							}`}
						>
						<span className="sr-only">Última página</span>
							<ChevronDoubleRightIcon className="h-4 w-4" />
						</button>
					</nav>
				
					{/* Input para saltar a página específica */}
					<div className="flex items-center ml-4">
						<span className="text-sm text-gray-700 mr-2">Ir a:</span>
						<input
							type="number"
							min="1"
							max={totalPages}
							value={currentPage}
							onChange={(e) => {
								const page = parseInt(e.target.value);
								if (page >= 1 && page <= totalPages) {
									goToPage(page);
								}
							}}
							className="w-16 py-1 px-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
						/>
						<span className="text-sm text-gray-700 ml-2">de {totalPages}</span>
					</div>
				</div>
			</div>
		</div>
	);

	// Componente de paginación para móvil
	const MobilePagination = () => (
		<div className="md:hidden bg-white border-t border-gray-200 px-4 py-3">
			<div className="flex items-center justify-between">
				<div className="flex-1 flex justify-between">
					<button
						onClick={goToPrevPage}
						disabled={currentPage === 1}
						className={`relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${
						currentPage === 1
							? 'bg-gray-100 text-gray-400 cursor-not-allowed'
							: 'bg-white text-gray-700 hover:bg-gray-50'
						}`}
					>
						<ChevronLeftIcon className="h-4 w-4 mr-1" />
						Anterior
					</button>
				
					<div className="flex items-center">
						<span className="text-sm text-gray-700 mx-3">
							Página {currentPage} de {totalPages}
						</span>
					</div>
				
					<button
						onClick={goToNextPage}
						disabled={currentPage === totalPages}
						className={`relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md ${
						currentPage === totalPages
							? 'bg-gray-100 text-gray-400 cursor-not-allowed'
							: 'bg-white text-gray-700 hover:bg-gray-50'
						}`}
					>
						Siguiente
						<ChevronRightIcon className="h-4 w-4 ml-1" />
					</button>
				</div>
			</div>
		
			<div className="mt-3 flex items-center justify-between">
				<div className="text-sm text-gray-700">
					Mostrando {startIndex + 1}-{endIndex} de {totalItems}
				</div>
				
				<div className="flex items-center">
					<label htmlFor="mobile-items-per-page" className="text-sm text-gray-700 mr-2">
						Por página:
					</label>
					<select
						id="mobile-items-per-page"
						value={mobileItemsPerPage}
						onChange={(e) => setMobileItemsPerPage(Number(e.target.value))}
						className="block py-1 px-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
					>
						{[3, 5, 10].map((size) => (
						<option key={size} value={size}>
							{size}
						</option>
						))}
					</select>
				</div>
			</div>
		</div>
	);

	// Estado vacío
	if (companies.length === 0) {
		return (
			<div className="text-center py-12">
				<BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">No hay empresas</h3>
				<p className="mt-1 text-sm text-gray-500">Comienza creando tu primera empresa.</p>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg shadow-sm overflow-hidden">
			{/* Contador de resultados para mobile */}
			<div className="md:hidden bg-gray-50 px-4 py-2 border-b border-gray-200">
				<p className="text-sm text-gray-600">
					Mostrando {startIndex + 1}-{endIndex} de {totalItems} empresas
				</p>
			</div>

			{/* Desktop Table */}
			<div className="hidden md:block overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								NIT
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Nombre
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Cantidad de Horas
							</th>
							{isManager &&
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Valor de la Hora
							</th>
							}
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Dirección
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Teléfono
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Email
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Contacto
							</th>
							<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Acciones
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{currentCompanies.map((company) => (
							<tr key={company.nit} className="hover:bg-gray-50">
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm font-medium text-gray-900">{company.nit}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-900">{company.nombre}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-900">{company.cantidad_horas}</div>
								</td>
								{isManager &&
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-900">$ {Number(company.valor_hora || 0).toLocaleString('es-CO')}</div>
								</td>
								}
								<td className="px-6 py-4">
									<div className="text-sm text-gray-900 max-w-xs truncate">{company.direccion}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-900">{company.telefono}</div>
								</td>
								<td className="px-6 py-4">
									<div className="text-sm text-gray-900 max-w-xs truncate">{company.email}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-gray-900">{company.contacto}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
									<div className="flex space-x-2">
										<button
											onClick={() => onEdit(company)}
											className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
											title="Editar empresa"
										>
											<PencilIcon className="h-4 w-4" />
										</button>
										<button
											onClick={() => handleDelete(company)}
											className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
											title="Eliminar empresa"
										>
											<TrashIcon className="h-4 w-4" />
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Mobile Cards */}
			<div className="md:hidden space-y-4 p-4">
				{currentCompanies.map((company) => (
					<div key={company.nit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
						<div className="flex items-start justify-between mb-3">
							<div className="flex-1 min-w-0">
								<div className="flex items-center space-x-2 mb-1">
									<BuildingOfficeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
									<h3 className="text-lg font-semibold text-gray-900 truncate">{company.nombre}</h3>
								</div>
									<p className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
									{company.nit}
								</p>
							</div>
							<div className="flex space-x-1 ml-2 flex-shrink-0">
								<button
									onClick={() => onEdit(company)}
									className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
									title="Editar empresa"
								>
									<PencilIcon className="h-4 w-4" />
								</button>
								<button
									onClick={() => handleDelete(company)}
									className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
									title="Eliminar empresa"
								>
									<TrashIcon className="h-4 w-4" />
								</button>
							</div>
						</div>

						<div className="space-y-2 text-sm">
							<div className="flex items-start">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Horas:</span>
								<span className="text-gray-900 flex-1">{company.cantidad_horas}</span>
							</div>
							{isManager &&
							<div className="flex items-start">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Valor:</span>
								<span className="text-gray-900 flex-1">{company.valor_hora}</span>
							</div>
							}
							<div className="flex items-start">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Dirección:</span>
								<span className="text-gray-900 flex-1">{company.direccion}</span>
							</div>
						
							<div className="flex items-center">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Teléfono:</span>
								<span className="text-gray-900">{company.telefono}</span>
							</div>
						
							<div className="flex items-center">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Email:</span>
								<a 
									href={`mailto:${company.email}`}
									className="text-blue-600 hover:text-blue-800 truncate"
								>
									{company.email}
								</a>
							</div>
						
							<div className="flex items-center">
								<span className="text-gray-500 font-medium w-20 flex-shrink-0">Contacto:</span>
								<span className="text-gray-900">{company.contacto}</span>
							</div>

							{/* Información de contacto secundaria si existe */}
							{(company.telefono_secundario || company.contacto_secundario || company.email_secundario) && (
								<div className="pt-2 mt-2 border-t border-gray-100">
									<p className="text-xs font-medium text-gray-500 mb-1">Contacto Secundario:</p>
									<div className="space-y-1 pl-2">
										{company.telefono_secundario && (
											<div className="flex items-center text-xs">
												<span className="text-gray-500 w-16 flex-shrink-0">Tel:</span>
												<span className="text-gray-700">{company.telefono_secundario}</span>
											</div>
										)}
										{company.contacto_secundario && (
											<div className="flex items-center text-xs">
												<span className="text-gray-500 w-16 flex-shrink-0">Contacto:</span>
												<span className="text-gray-700">{company.contacto_secundario}</span>
											</div>
										)}
										{company.email_secundario && (
											<div className="flex items-center text-xs">
												<span className="text-gray-500 w-16 flex-shrink-0">Email:</span>
												<a 
													href={`mailto:${company.email_secundario}`}
													className="text-blue-600 hover:text-blue-800 truncate"
												>
													{company.email_secundario}
												</a>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Componentes de paginación */}
			<DesktopPagination />
			<MobilePagination />
		</div>
	);
};

export default CompanyList;